import type { PropsWithChildren } from "react"
import type React from "react"
import { createContext, useContext, useMemo } from "react"
import { parseFeatureFlagData } from "../interfaces/featureFlagContext"
import { parsePerformToggleData } from "../interfaces/performToggleContext"
import type { IAppContext } from "../interfaces/appContext"
import type { IFeatureFlagData } from "../interfaces/featureFlagContext"
import type { IPerformToggleData } from "../interfaces/performToggleContext"

interface IAppContextInput<Context extends IAppContext> {
    $reactAppContext: Context
    active_feature_flags?: string
    active_perform_toggles?: string
}

type IAppContextData<Context extends IAppContext> = {
    context: Context
    flags: IFeatureFlagData
    toggles: IPerformToggleData
}

const AppContext = createContext<IAppContextData<IAppContext>>(
    undefined as unknown as IAppContextData<IAppContext>,
)

export function AppContextProvider<Context extends IAppContext>({
    children,
    context,
}: PropsWithChildren<{ context: IAppContextInput<Context> }>) {
    const contextValue = useMemo(() => {
        return {
            context: context.$reactAppContext,
            flags: parseFeatureFlagData(context.active_feature_flags),
            toggles: parsePerformToggleData(context.active_perform_toggles),
        }
    }, [
        context.$reactAppContext,
        context.active_feature_flags,
        context.active_perform_toggles,
    ])
    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    )
}

export function useAppContext<
    Context extends IAppContext,
>(): IAppContextData<Context> {
    const context = useContext<IAppContextData<Context>>(
        AppContext as unknown as React.Context<IAppContextData<Context>>,
    )
    if (!context) {
        throw new Error("useAppContext must be used under AppContextProvider")
    }
    return context
}

export type FeatureFlag = {
    name: string
    active: boolean
}

export function useFeatureFlag(name: string): FeatureFlag {
    const { flags } = useAppContext()
    return {
        name: name,
        active: flags.active.has(name),
    }
}

export type PerformToggle = {
    name: string
    active: boolean
}

export function usePerformToggle(name: string): PerformToggle {
    const { toggles } = useAppContext()
    return {
        name: name,
        active: toggles.active.has(name),
    }
}
